﻿import { NumericTextField } from "@/shared/components/NumericTextField";
import { PercentField } from "@/shared/components/PercentField";
import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { useCalendarAreaId } from "../../context/CalendarAreaContext";
import { ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Grid2,
	InputAdornment,
	Typography,
} from "@mui/material";
import { round } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSearchEvents } from "../../hooks/useSearchEvents";
import { Event } from "../../models/Event";
import {
	upsertProductionEvent,
	upsertProductionEventActuals,
} from "../../repositories/CalendarRepository";
import {
	calculateFinishedYield,
	calculateMillYield,
	calculateRollingHours,
} from "../../utils/calculateFields";
import { EventComparisonTable } from "../eventComparisonTable/EventComparisonTable";
import { useMutation } from "@tanstack/react-query";

export const ConfirmEventModal = ({
	selectedEventId,
	open,
	onHide,
	isDraftMode,
	monthStart,
}: {
	selectedEventId: number | null;
	open: boolean;
	onHide: (update: boolean) => void;
	isDraftMode: boolean;
	monthStart?: Date | null;
}) => {
	const defaultAreaId = useCalendarAreaId();
	const [fieldsPrefilled, setFieldsPrefilled] = useState<
		Record<string, "autocalculated" | boolean>
	>({
		actualChargedTons: false,
		actualRolledTons: false,
		actualFinishedTons: false,
		actualTPH: false,
		actualRollingHours: false,
		actualMillYield: false,
		actualFinishedYield: false,
	});
	const [error, setError] = useState("");

	const { data: events } = useSearchEvents({
		eventId: selectedEventId ?? undefined,
		monthStart,
		stopRefetch: true,
		isDraftMode,
	});
	const event = events?.find(
		(e) => e.EventId === selectedEventId,
	) as Event | null;

	const {
		control,
		reset,
		getValues,
		setValue,
		handleSubmit,
		formState: { isSubmitting },
	} = useForm<Event>({
		mode: "all",
	});

	const upsertProductionActualsMutation = useMutation(
		upsertProductionEventActuals,
		{
			onSuccess: async () => {
				onHide(true);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);
	const upsertProductionMutation = useMutation(upsertProductionEvent, {
		onSuccess: async () => {
			if (monthStart && getValues("EventId")) {
				await upsertProductionActualsMutation.mutateAsync({
					eventId: getValues("EventId"),
					monthStart,
					actualChargedTons: getValues("ActualChargedTons"),
					actualRolledTons: getValues("ActualRolledTons"),
					actualFinishedTons: getValues("ActualFinishedTons"),
					actualTPH: getValues("ActualTPH"),
					actualRollingHours: getValues("ActualRollingHours"),
					actualRollChangeHours: getValues("ActualRollChangeHours"),
					actualMillYield: getValues("ActualMillYield"),
					actualFinishedYield: getValues("ActualFinishedYield"),
					defaultAreaId,
				});
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});
	const onSubmit: SubmitHandler<Event> = async (data) => {
		await upsertProductionMutation.mutateAsync({
			eventId: data?.EventId,
			start: data.Start!,
			end: data.End ?? null,
			sectionId: data.SectionId,
			statusCode: "K",
			inquiryStatusCode: data.InquiryStatusCode,
			billetsStatusCode: data.BilletsStatusCode,
			deadline: data.Deadline,
			targetTPH: data.TargetTPH,
			scheduledChargedTons: data.ScheduledChargedTons,
			scheduledRolledTons: data.ScheduledRolledTons,
			scheduledFinishedTons: data.ScheduledFinishedTons,
			budgetTPH: data.BudgetTPH,
			scheduledRollingHours: data.ScheduledRollingHours,
			scheduledRollChangeHours: data.ScheduledRollChangeHours,
			scheduledMillYield: data.ScheduledMillYield,
			scheduledFinishedYield: data.ScheduledFinishedYield,
			actualChargedTons: data.ActualChargedTons,
			actualRolledTons: data.ActualRolledTons,
			actualFinishedTons: data.ActualFinishedTons,
			actualTPH: data.ActualTPH,
			actualRollingHours: data.ActualRollingHours,
			actualRollChangeHours: data.ActualRollChangeHours,
			actualMillYield: data.ActualMillYield,
			actualFinishedYield: data.ActualFinishedYield,
			comments: data.EventComments,
			isDraftMode,
			defaultAreaId,
		});
	};

	const onYieldUpdate = (
		field: "ActualMillYield" | "ActualFinishedYield",
		value: number | null,
	) => {
		setValue(field, value);
		setFieldsPrefilled((prev) => ({
			...prev,
			[field.charAt(0).toLowerCase() + field.slice(1)]: "autocalculated",
		}));
	};

	const onRollingHoursUpdate = (value: number) => {
		setValue("ActualRollingHours", value);
		setFieldsPrefilled((prev) => ({
			...prev,
			actualRollingHours: "autocalculated",
		}));
	};

	useEffect(() => {
		if (open && event) {
			reset(event);

			const newFieldsPrefilled: Record<string, "autocalculated" | boolean> = {
				actualChargedTons: false,
				actualRolledTons: false,
				actualFinishedTons: false,
				actualTPH: false,
				actualRollingHours: false,
				actualMillYield: false,
				actualFinishedYield: false,
			};

			if (
				!event.ActualMillYield &&
				event.ActualChargedTons &&
				event.ActualRolledTons
			) {
				const calculatedMillYield = calculateMillYield(
					event.ActualChargedTons ?? 1,
					event.ActualRolledTons ?? 0,
				);
				setValue("ActualMillYield", calculatedMillYield);
				newFieldsPrefilled.actualMillYield = "autocalculated";
			}

			if (
				!event.ActualFinishedYield &&
				event.ActualChargedTons &&
				event.ActualFinishedTons
			) {
				const calculatedFinishedYield = calculateFinishedYield(
					event.ActualFinishedTons ?? 0,
					event.ActualChargedTons ?? 1,
				);
				setValue("ActualFinishedYield", calculatedFinishedYield);
				newFieldsPrefilled.actualFinishedYield = "autocalculated";
			}

			if (
				!event.ActualRollingHours &&
				event.ActualRolledTons &&
				event.ActualTPH
			) {
				const calculatedRollingHours = calculateRollingHours(
					event.ActualRolledTons ?? 0,
					event.ActualTPH ?? 1,
				);
				setValue("ActualRollingHours", round(Number(calculatedRollingHours)));
				newFieldsPrefilled.actualRollingHours = "autocalculated";
			}

			setFieldsPrefilled(newFieldsPrefilled);
		}
	}, [event, open]);

	const allFieldsFilled = () => {
		const value =
			!!getValues("ActualChargedTons") &&
			!!getValues("ActualRolledTons") &&
			!!getValues("ActualFinishedTons") &&
			!!getValues("ActualTPH") &&
			!!getValues("ActualRollingHours") &&
			!!getValues("ActualMillYield") &&
			!!getValues("ActualFinishedYield");

		return value;
	};

	return (
		<>
			<MesfModal
				maxWidth="xl"
				open={open}
				handleClose={() => onHide(false)}
				title="Confirm Event"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 size={{ md: 12 }} sx={{ marginBottom: 2 }}>
							<EventComparisonTable event={event} />
						</Grid2>

						<Grid2 size={{ md: 12 }}>
							<Accordion defaultExpanded data-test-id="accordion-actual">
								<AccordionSummary
									expandIcon={
										<ExpandMore sx={{ cursor: "pointer !important" }} />
									}
									sx={{
										cursor: "default !important",
									}}
								>
									<Typography variant="h6" color="textSecondary" fontSize={14}>
										Actual
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Grid2 container spacing={2} width="100%">
										<Grid2 size={{ md: 4 }}>
											<Controller
												name="ActualChargedTons"
												control={control}
												render={({ field }) => (
													<NumericTextField
														label="Charged Tons"
														value={field.value}
														onChange={(value: number | null) => {
															field.onChange(value);
															const finishedTons =
																getValues("ActualFinishedTons");
															const rolledTons = getValues("ActualRolledTons");

															const newMillYield = calculateMillYield(
																value ?? 1,
																rolledTons ?? 0,
															);
															const newFinishedYield = calculateFinishedYield(
																finishedTons ?? 0,
																value ?? 1,
															);

															onYieldUpdate("ActualMillYield", newMillYield);
															onYieldUpdate(
																"ActualFinishedYield",
																newFinishedYield,
															);
														}}
														showDecimals
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 4 }}>
											<Controller
												name="ActualRolledTons"
												control={control}
												render={({ field }) => (
													<NumericTextField
														label="Rolled Tons"
														value={field.value}
														onChange={(value: number | null) => {
															field.onChange(value);
															const chargedTons =
																getValues("ActualChargedTons");
															const rollingHours =
																getValues("ActualRollingHours");

															const newMillYield = calculateMillYield(
																chargedTons ?? 0,
																value ?? 1,
															);
															onYieldUpdate("ActualMillYield", newMillYield);

															const newTPH = calculateRollingHours(
																value ?? 0,
																rollingHours ?? 1,
															);
															setValue("ActualTPH", Number(newTPH));
															setFieldsPrefilled((prev) => ({
																...prev,
																actualTPH: "autocalculated",
															}));
														}}
														showDecimals
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 4 }}>
											<Controller
												name="ActualFinishedTons"
												control={control}
												render={({ field }) => (
													<NumericTextField
														label="Finished Tons"
														value={field.value}
														onChange={(value: number | null) => {
															field.onChange(value);
															const chargedTons =
																getValues("ActualChargedTons");

															const newFinishedYield = calculateFinishedYield(
																value ?? 0,
																chargedTons ?? 1,
															);
															onYieldUpdate(
																"ActualFinishedYield",
																newFinishedYield,
															);
														}}
														showDecimals
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 3 }}>
											<Controller
												name="ActualTPH"
												control={control}
												render={({ field }) => (
													<NumericTextField
														label="Tons Per Hour"
														value={field.value}
														onChange={field.onChange}
														disabled
														showDecimals
														helperText={
															fieldsPrefilled.actualTPH === "autocalculated"
																? "Autocalculated (Rolled / Hours)"
																: ""
														}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 3 }}>
											<Controller
												name="ActualRollingHours"
												control={control}
												render={({ field }) => (
													<NumericTextField
														label="Rolling Hours"
														value={field.value}
														onChange={(value: number | null) => {
															field.onChange(value);
															const rolledTons = getValues("ActualRolledTons");

															const newTPH = calculateRollingHours(
																rolledTons ?? 0,
																value ?? 1,
															);
															setValue("ActualTPH", Number(newTPH));
															setFieldsPrefilled((prev) => ({
																...prev,
																actualTPH: "autocalculated",
															}));
														}}
														showDecimals={false}
														InputProps={{
															endAdornment: (
																<InputAdornment position="end">
																	hs
																</InputAdornment>
															),
														}}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 3 }}>
											<Controller
												name="ActualMillYield"
												control={control}
												rules={{
													min: {
														value: 0,
														message: "Must be at least 0%",
													},
													max: {
														value: 100,
														message: "Must be less than or equal to 100%",
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<PercentField
														label="Mill Yield"
														value={field.value}
														onChange={field.onChange}
														disabled
														error={!!error}
														helperText={
															error?.message ||
															(fieldsPrefilled.actualMillYield ===
															"autocalculated"
																? "Autocalculated (Charge Tons / Rolled Tons)"
																: "")
														}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 3 }}>
											<Controller
												name="ActualFinishedYield"
												control={control}
												rules={{
													min: {
														value: 0,
														message: "Must be at least 0%",
													},
													max: {
														value: 100,
														message: "Must be less than or equal to 100%",
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<PercentField
														label="Finished Yield"
														value={field.value}
														onChange={field.onChange}
														error={!!error}
														disabled
														helperText={
															error?.message ||
															(fieldsPrefilled.actualFinishedYield ===
															"autocalculated"
																? "Autocalculated (Finished Tons / Charged Tons)"
																: "")
														}
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</AccordionDetails>
							</Accordion>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions sx={{ px: 4 }}>
						<Grid2
							container
							size={12}
							spacing={1}
							justifyContent="flex-end"
							alignItems="center"
							sx={{ width: "100%" }}
						>
							<Grid2 size={{ xs: 3 }}>
								<Button
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
									fullWidth
								>
									Close
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 3 }}>
								<ButtonWithLoading
									isLoading={isSubmitting}
									variant="contained"
									color="primary"
									type="submit"
									fullWidth
									disabled={!allFieldsFilled()}
									data-test-id="confirm-event-button"
								>
									Confirm
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
